import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';

import { Button } from '@/components/ui/daisy/button';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

import { getPaginationRange } from './getPaginationRange';
import {
  PaginationControlProps,
  PaginationItemProps,
  PaginationProps,
} from './types';

const PaginationBase = forwardRef<HTMLDivElement, PaginationProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('join', className)} {...props}>
      {children}
    </div>
  ),
);

const Item = forwardRef<HTMLButtonElement, PaginationItemProps>(
  ({ className, ...props }, ref) => (
    <Button ref={ref} className={twMerge('join-item', className)} {...props} />
  ),
);

const Control = forwardRef<HTMLDivElement, PaginationControlProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      siblingCount = 1,
      showPrevNext = true,
      size,
      color,
      variant,
      labels,
      ...props
    },
    ref,
  ) => {
    const pages = getPaginationRange({ currentPage, totalPages, siblingCount });

    return (
      <PaginationBase ref={ref} {...props}>
        <ConditionalRenderer condition={showPrevNext}>
          <Item
            size={size}
            variant={variant}
            aria-label={labels?.previous}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <ChevronLeftIcon className="size-3.5" />
          </Item>
        </ConditionalRenderer>

        {pages.map((page, index) =>
          page === 'ellipsis' ? (
            <Item
              key={`ellipsis-${index}`}
              size={size}
              color={color}
              variant={variant}
              disabled
            >
              …
            </Item>
          ) : (
            <Item
              key={page}
              size={size}
              color={page === currentPage ? color : 'default'}
              variant={variant}
              active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </Item>
          ),
        )}

        <ConditionalRenderer condition={showPrevNext}>
          <Item
            size={size}
            variant={variant}
            aria-label={labels?.next}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <ChevronRightIcon className="size-3.5" />
          </Item>
        </ConditionalRenderer>
      </PaginationBase>
    );
  },
);

PaginationBase.displayName = 'Pagination';
Item.displayName = 'Pagination.Item';
Control.displayName = 'PaginationControl';

export const Pagination = Object.assign(PaginationBase, {
  Item,
});

export const PaginationControl = Control;

export { getPaginationRange };
